/*
 * bc-diagrams — the shared <bc-diagram> custom element behind the PDP
 * product diagrams (blocks/bc-diagram.liquid + the sticky-cart mini in
 * sections/product-information.liquid). One element serves every diagram
 * shape: the block picks an SVG body by product type (webbing line with
 * sewn loops, round-sling oval, future shapes), and shape-specific nodes
 * that are absent simply no-op. To add a shape: new bc-diagram-<shape>-svg
 * snippet + a branch in the block; only add JS here if the new body needs
 * its own responsive/update behavior (cf. slingOvalEl).
 *
 * Webbing body — live preview of a configured webbing sling. Reads the checked
 * radios / selected dropdown values for three configured variant option
 * names (Length, Sewn Loop End 1, Sewn Loop End 2) and updates an SVG with:
 *   - the selected length value as text above the line
 *   - a sewn-loop hairpin drawn at each end of the line (the segment
 *     scene's construction — see bc-diagram-webbing-svg.liquid), hidden /
 *     styled differently per the selected end value:
 *       none    → no loop drawn
 *       naked   → hairpin stroked in the webbing color, line thickness
 *       sleeved → naked look + sleeve-color overlay on the outer half
 *
 * Sewn-loop ends are identified three ways, in order of preference:
 *   1. The bc-end-treatment block's line-item-property selectors
 *      (`[data-bc-end-role]` + each input's `data-kind`). This is the
 *      post-migration source, where ends are line item properties instead
 *      of variant options.
 *   2. A `[data-bc-sewn-loop-map]` JSON script tag emitted by the Liquid
 *      block when the product's options are linked to the sewn_loop_end
 *      metaobject. JS reads the kind (none/naked/sleeved) directly from
 *      that map keyed by the selected radio's data-option-value-id.
 *   3. Fallback: match the fieldset `<legend>` text (case-insensitive) to
 *      the configured option-name strings, and lowercase the selected
 *      value's text to derive the kind. Used for products that haven't
 *      been migrated to the metaobject yet.
 *
 * Round-sling body — no loops/sleeves; the `.bc-diagram__sling-oval` rect,
 * when present, is stretched on responsive resize so both rounded ends stay
 * anchored to the viewBox edges, and a trailing parenthetical in the variant
 * value ("4 ft (1.2 m)") is dropped from the primary readout.
 *
 * Chain body — a `.bc-diagram__chain` group (bc-diagram-chain-svg snippet)
 * instead of line + loops: N stadium ovals joined by N-1 connector links.
 * Rebuilt from scratch on every update so the link count and connector
 * stretch always fill the (possibly resized) span exactly — keep the
 * algorithm in lockstep with the snippet's static Liquid render.
 *
 * Variant color — when a `script[data-bc-variant-color-map]` child is
 * present ({"option":"Color","colors":{value: hex}}), the selected color
 * variant's hex re-strokes every [data-bc-vc] node (webbing body) and
 * becomes the chain build color. No map -> the Liquid-rendered colors are
 * never touched.
 *
 * Update scheduling — _update() is a full SVG rebuild (chain links are
 * recreated from scratch and _updateDimension measures the length label with
 * getBBox), and a webbing PDP carries two instances (the block diagram + the
 * sticky-bar mini) driven by four triggers: `change`, `bc:custom-length:
 * change` and `quantity-selector:update` on the section scope, plus
 * `variant:update` on the document. Two things keep that from turning one
 * click into several rebuilds per element:
 *
 *   1. COALESCING — every trigger calls _scheduleUpdate(), which renders once
 *      on the next animation frame. Handlers still record their payload
 *      synchronously (see _onCustomLength), so the frame renders the LAST
 *      state received, never a dropped one. The first render, from
 *      connectedCallback, stays synchronous so the element paints in step
 *      with the server-rendered markup around it.
 *
 *   2. CHANGE GUARD — the render inputs are joined into a key that is stamped
 *      on the element as data-bc-render-key; a pass whose key still matches
 *      returns before touching the DOM. That kills the redundant rebuild from
 *      `variant:update` (which lands a section fetch later, in its own frame,
 *      restating the `change` we already drew). Stamping it in the DOM rather
 *      than in a field is deliberate: assets/morph.js drops attributes the
 *      server render doesn't carry, so a variant morph that rewrites this
 *      subtree also wipes the key — exactly when the cached "nothing changed"
 *      would be a lie, and we re-render.
 */
(() => {
  if (customElements.get('bc-diagram')) return;

  const END_NAKED = 'naked';
  const END_SLEEVED = 'sleeved';

  // Attribute holding the last rendered state key. See the header comment:
  // it lives in the DOM so a morph that resets the diagram also resets it.
  const RENDER_KEY_ATTR = 'data-bc-render-key';
  // Field separator for that key — a character no option value, unit or hex
  // can contain, so no two different states can join to the same string.
  const KEY_SEP = '\u0001';

  // Group thousands with commas for the on-diagram readouts. Pinned to the
  // en-US locale so the separator is always a comma regardless of the
  // visitor's locale, and capped at the default 3 fraction digits so whole
  // lengths stay integer while custom/fractional lengths keep their decimals.
  const formatNum = (n) => Number(n).toLocaleString('en-US');

  class BcDiagram extends HTMLElement {
    constructor() {
      super();
      this._update = this._update.bind(this);
      this._scheduleUpdate = this._scheduleUpdate.bind(this);
    }

    connectedCallback() {
      this.svgEl = this.querySelector('.bc-diagram__svg');
      this.lengthEl = this.querySelector('.bc-diagram__length');
      this.secondaryEl = this.querySelector('.bc-diagram__length-secondary');
      // NodeList — supports both single-color (one .bc-diagram__line)
      // and two-tone (upper + lower halves, both carrying the class) renders.
      this.lineEls = this.querySelectorAll('.bc-diagram__line');
      // End nodes are now <g> wrappers around a base path + a sleeve-
      // overlay path (data-sleeve). Visibility toggles cascade through
      // the group; _applyEnd just flips hidden on the group + overlay.
      this.end1El = this.querySelector('.bc-diagram__end1');
      this.end2El = this.querySelector('.bc-diagram__end2');
      this.overlapLeftEl = this.querySelector('.bc-diagram__overlap-left');
      this.overlapRightEl = this.querySelector('.bc-diagram__overlap-right');
      this.weightEl = this.querySelector('[data-key="weight"]');
      this.weightValueEl = this.querySelector('[data-key="weight-value"]');
      // Chain diagrams only (bc-diagram-slots snippet): the slot-count
      // readout + the slot period (slot length + spacing, mm) it derives
      // from. Null/0 elsewhere — _renderSlots no-ops.
      this.slotsEl = this.querySelector('[data-key="slots"]');
      this.slotsValueEl = this.querySelector('[data-key="slots-value"]');
      this.slotPeriodMm = parseFloat(this.slotsEl?.dataset.slotPeriodMm) || 0;
      // Tracked separately from the SVG viewBox attribute so the
      // dimension / line / center-anchored math can stay in viewBox
      // units. Defaults to the snippet's hard-coded 480 so non-
      // responsive instances (sticky-cart mini, etc.) keep the
      // original right-edge / center coords without any JS resize.
      this._viewBoxWidth = 480;
      // viewBox height differs per SVG body (webbing 140, sling 150) —
      // read it from the markup so _applyResponsiveLayout preserves it.
      const vbParts = (this.svgEl?.getAttribute('viewBox') || '').trim().split(/\s+/);
      this._viewBoxHeight = parseFloat(vbParts[3]) || 140;
      // Round-sling body: a single stadium-shaped rect instead of
      // line + loops. Null on webbing renders.
      this.slingOvalEl = this.querySelector('.bc-diagram__sling-oval');
      // Chain body: rebuilt in _rebuildChain on every update. Null on
      // webbing / sling renders.
      this.chainEl = this.querySelector('.bc-diagram__chain');

      // Variant-color map ({option, colors}) — see the header comment.
      this._colorMap = null;
      try {
        const parsed = JSON.parse(
          this.querySelector('script[data-bc-variant-color-map]')?.textContent || ''
        );
        if (parsed && parsed.colors && typeof parsed.colors === 'object') {
          this._colorMap = parsed;
        }
      } catch (_e) {
        this._colorMap = null;
      }

      this.dimGroup = this.querySelector('.bc-diagram__dimension');
      this.dimLineLeft = this.querySelector('.bc-diagram__dim-line-left');
      this.dimLineRight = this.querySelector('.bc-diagram__dim-line-right');
      this.dimExtLeft = this.querySelector('.bc-diagram__dim-ext-left');
      this.dimExtRight = this.querySelector('.bc-diagram__dim-ext-right');
      this.dimArrowLeft = this.querySelector('.bc-diagram__dim-arrow-left');
      this.dimArrowRight = this.querySelector('.bc-diagram__dim-arrow-right');

      this.lengthOption = (this.dataset.lengthOption || 'Length').trim();
      this.end1Option = (this.dataset.end1Option || 'Sewn Loop - End 1').trim();
      this.end2Option = (this.dataset.end2Option || 'Sewn Loop - End 2').trim();
      this.lineColor = this.dataset.lineColor || '#2b2b2b';
      this.sleevedColor = this.dataset.sleevedColor || '#c54a2c';
      this.lengthUnit = this.dataset.lengthUnit || '';
      this.lineThickness = parseFloat(this.dataset.lineThickness) || 6;
      this.sleeveThickness = parseFloat(this.dataset.sleeveThickness) || 14;
      // Compact instances (sticky-cart mini) push each loop outward by
      // loopOffset viewBox units so the loops sit closer to the rendered
      // SVG edges. Static SVG transform on the end groups + matching shift
      // on the overlap stripes + line endpoint adjustment in _lineX keep
      // the loop / line / stripe join flush. Default 0 = no shift.
      // Mutually exclusive with responsive_line: responsive mode rewrites
      // end2 / overlap-right transforms on every resize, which would
      // clobber the static loop_offset shift and leave the right side
      // misaligned. Zero out loop_offset here so _lineX and the transform
      // block downstream stay consistent (both no-op).
      this.loopOffset = parseFloat(this.dataset.loopOffset) || 0;
      if (this.loopOffset !== 0 && this.dataset.responsiveLine === 'true') {
        console.warn(
          '[bc-diagram] loop_offset is ignored when responsive_line is true'
        );
        this.loopOffset = 0;
      }
      this.secondaryEnabled = this.dataset.secondaryEnabled === 'true';
      this.secondaryUnit = this.dataset.secondaryUnit || '';
      const rawFactor = this.dataset.secondaryFactor;
      const parsedFactor = parseFloat(rawFactor);
      if (rawFactor && !Number.isFinite(parsedFactor)) {
        console.warn(
          '[bc-diagram] secondary_factor is not a number; secondary length will be hidden:',
          rawFactor
        );
      }
      this.secondaryFactor = Number.isFinite(parsedFactor) && parsedFactor > 0 ? parsedFactor : 0;

      this.weightEnabled = this.dataset.weightEnabled === 'true';
      this.weightPrimaryUnit = this.dataset.weightPrimaryUnit || 'kg';
      this.weightSecondaryUnit = this.dataset.weightSecondaryUnit || '';

      try {
        const parsed = JSON.parse(
          this.querySelector('script[data-bc-variant-weights]')?.textContent || '{}'
        );
        this._weights = parsed.weights || {};
        this._byOptions = parsed.byOptions || {};
      } catch (_e) {
        this._weights = {};
        this._byOptions = {};
      }

      // sewn_loop_end metaobject link map. Each entry pins a fieldset by
      // option position and exposes value-id -> kind. Present only on
      // migrated products; absent products fall through to the legacy
      // name/text matching below.
      this._sewnLoopMap = null;
      try {
        const parsed = JSON.parse(
          this.querySelector('script[data-bc-sewn-loop-map]')?.textContent || '{}'
        );
        const entries = Array.isArray(parsed.options) ? parsed.options : [];
        const byRole = {};
        for (const entry of entries) {
          if (entry && (entry.role === 'end1' || entry.role === 'end2')) {
            byRole[entry.role] = {
              position: Number(entry.position) || null,
              valueMap: entry.valueMap || {},
            };
          }
        }
        if (byRole.end1 || byRole.end2) this._sewnLoopMap = byRole;
      } catch (_e) {
        this._sewnLoopMap = null;
      }

      // Inside the quick-add modal the diagram is morphed in without its
      // section ancestor, so scope to the dialog first; without this we'd
      // fall through to `document` and listen for bc:custom-length:change
      // events from the actual product page below the modal.
      this._scope =
        this.closest('quick-add-dialog') ||
        this.closest('.shopify-section') ||
        this.closest('product-information') ||
        document;

      this._currentVariantId = this._scope.querySelector('input[name="id"]')?.value || null;

      this._updateRaf = 0;
      this._pendingViewBox = null;

      this._customLength = null;
      this._customLengthActive = false;
      this._customLengthPieces = 1;
      this._onCustomLength = (e) => {
        const d = e?.detail;
        if (!d) return;
        // Payload recorded synchronously, render deferred: several events in
        // one frame (the sticky bar's mirror inputs echo the block's, and a
        // length + pieces edit can land together) collapse into one render of
        // the last state.
        this._customLengthActive = Boolean(d.active);
        this._customLength = d.active ? d.length : null;
        this._customLengthUnit = d.active ? d.unit || '' : '';
        this._customLengthPieces = d.active && Number.isFinite(d.pieces) ? d.pieces : 1;
        this._scheduleUpdate();
      };

      this._scope.addEventListener('change', this._scheduleUpdate);
      this._scope.addEventListener('bc:custom-length:change', this._onCustomLength);
      this._scope.addEventListener('quantity-selector:update', this._scheduleUpdate);
      document.addEventListener('variant:update', this._scheduleUpdate);

      if (this.loopOffset !== 0) {
        if (this.end1El) this.end1El.setAttribute('transform', `translate(${-this.loopOffset} 0)`);
        if (this.overlapLeftEl) this.overlapLeftEl.setAttribute('transform', `translate(${-this.loopOffset} 0)`);
        if (this.end2El) this.end2El.setAttribute('transform', `translate(${this.loopOffset} 0)`);
        if (this.overlapRightEl) this.overlapRightEl.setAttribute('transform', `translate(${this.loopOffset} 0)`);
      }

      // Responsive-line variant: track the SVG's rendered width and
      // rewrite the viewBox so 1 viewBox unit stays at 0.75 CSS px
      // regardless of column width. Loops/text/stroke keep their
      // original size; only the line stretches.
      if (this.dataset.responsiveLine === 'true' && this.svgEl && typeof ResizeObserver !== 'undefined') {
        // Read in the observer callback (layout is clean when it runs), write
        // in the scheduled frame — the old shape measured, wrote the viewBox,
        // then had _update measure the label with getBBox, forcing a second
        // layout inside the same callback.
        this._resizeObserver = new ResizeObserver(() => {
          if (this._measureResponsive()) this._scheduleUpdate();
        });
        this._resizeObserver.observe(this.svgEl);
        this._measureResponsive();
      }

      // First render is synchronous — the element paints in step with the
      // server-rendered markup around it, and no-JS parity is preserved.
      this._update();
    }

    disconnectedCallback() {
      if (this._scope) {
        this._scope.removeEventListener('change', this._scheduleUpdate);
        this._scope.removeEventListener('bc:custom-length:change', this._onCustomLength);
        this._scope.removeEventListener('quantity-selector:update', this._scheduleUpdate);
      }
      document.removeEventListener('variant:update', this._scheduleUpdate);
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._updateRaf) {
        cancelAnimationFrame(this._updateRaf);
        this._updateRaf = 0;
      }
    }

    /** One render per frame, whatever fired (see the header comment). */
    _scheduleUpdate() {
      if (this._updateRaf) return;
      this._updateRaf = requestAnimationFrame(() => {
        this._updateRaf = 0;
        this._update();
      });
    }

    /**
     * READ half of the responsive-line pass: measure the SVG's rendered
     * width and park the derived viewBox width. Returns true when it changed
     * (i.e. there is something to write).
     */
    _measureResponsive() {
      if (!this.svgEl) return false;
      const cssWidth = this.svgEl.clientWidth;
      if (!cssWidth) return false;
      // viewBox width = cssWidth * 4/3 keeps preserveAspectRatio's
      // uniform scale at 0.75 (= 105 CSS / 140 viewBox), so the
      // rendered loop / stroke / text-position sizes match the
      // original 360-cap render at every column width.
      const viewBoxWidth = cssWidth * (4 / 3);
      // Compare against what WILL be applied (a parked pending value beats
      // the currently rendered one), and clear a parked value that a later
      // measurement makes moot — otherwise _update could apply a stale one.
      const applied = this._pendingViewBox != null ? this._pendingViewBox : this._viewBoxWidth;
      if (viewBoxWidth === applied) return false;
      if (viewBoxWidth === this._viewBoxWidth) {
        this._pendingViewBox = null;
        return false;
      }
      this._pendingViewBox = viewBoxWidth;
      return true;
    }

    /**
     * WRITE half of the responsive-line pass. Runs at the top of _update so
     * every geometry write for the frame happens together, before the one
     * layout read (_updateDimension's getBBox) at the end of it.
     */
    _applyResponsiveLayout() {
      const viewBoxWidth = this._pendingViewBox;
      this._pendingViewBox = null;
      if (viewBoxWidth == null || !this.svgEl) return;
      this._viewBoxWidth = viewBoxWidth;
      this.svgEl.setAttribute('viewBox', `0 0 ${viewBoxWidth} ${this._viewBoxHeight}`);

      // Translate the right loop + its overlap stripe to stay anchored
      // to the right edge; their paths were authored for a 480-unit-
      // wide viewBox ending at 465.
      const offset = viewBoxWidth - 480;
      if (this.end2El) {
        this.end2El.setAttribute('transform', `translate(${offset} 0)`);
      }
      if (this.overlapRightEl) {
        this.overlapRightEl.setAttribute('transform', `translate(${offset} 0)`);
      }
      // Sling oval: both rounded ends stay anchored 15 units inside the
      // viewBox edges; only the straight top/bottom segments stretch. The
      // rect path is the stroke centerline, hence the - lineThickness.
      if (this.slingOvalEl) {
        this.slingOvalEl.setAttribute('width', String(viewBoxWidth - 30 - this.lineThickness));
      }
      if (this.lengthEl) this.lengthEl.setAttribute('x', String(viewBoxWidth / 2));
      if (this.secondaryEl) this.secondaryEl.setAttribute('x', String(viewBoxWidth / 2));
    }

    _readQuantity() {
      const input = this._scope.querySelector?.('input[name="quantity"]');
      const v = parseInt(input?.value, 10);
      return Number.isFinite(v) && v > 0 ? v : 1;
    }

    _resolveVariantIdFromOptions() {
      // Delegate to the shared resolver (assets/bc-variant-resolve.js).
      return window.bcVariantResolve?.resolveVariantId(this._scope, this._byOptions) || null;
    }

    /** True when this instance renders a weight readout at all. */
    _hasWeightReadout() {
      return Boolean(this.weightEnabled && this.weightEl && this.weightValueEl);
    }

    /**
     * Resolve (and cache on the instance) the variant the weight readout
     * should use. Called from _update's read pass so the id can join the
     * change-guard key; _renderWeight then just consumes the cached value.
     *
     * Prefer the resolved option-combo id so the readout matches the radios
     * immediately on click; fall back to a live query of the hidden id input
     * (re-queried every render because the variant-picker section morph can
     * replace it under us).
     */
    _resolveWeightVariantId() {
      const resolvedId = this._resolveVariantIdFromOptions();
      if (resolvedId) {
        this._currentVariantId = resolvedId;
      } else {
        const liveInput = this._scope.querySelector('input[name="id"]');
        if (liveInput?.value) this._currentVariantId = liveInput.value;
      }
      return this._currentVariantId || '';
    }

    _renderWeight() {
      if (!this._hasWeightReadout()) return;

      const raw = this._currentVariantId ? this._weights[this._currentVariantId] : null;
      if (!Number.isFinite(raw)) {
        this.weightValueEl.textContent = '';
        this.weightEl.setAttribute('hidden', '');
        return;
      }

      let grams = raw;
      if (this._customLengthActive && Number.isFinite(this._customLength) && this._customLength > 0) {
        // Variant weights on custom-length products are grams PER METER.
        // Multiply by pieces too so the readout matches the length label
        // ("10 m × 3") and the cart line — total order weight, not one piece.
        grams = grams * this._customLength * (this._customLengthPieces || 1);
      }

      const fmt = window.bcWeightFormat;
      if (!fmt) {
        // Don't leave a stale weight readout if the formatter isn't loaded
        // yet (load order normally guarantees it is; this is belt-and-braces).
        this.weightValueEl.textContent = '';
        this.weightEl.setAttribute('hidden', '');
        return;
      }
      const primary = fmt.fromGrams(grams, this.weightPrimaryUnit);
      let text = fmt.formatWeight(primary, this.weightPrimaryUnit);
      if (this.weightSecondaryUnit) {
        const secondary = fmt.fromGrams(grams, this.weightSecondaryUnit);
        text += ` (${fmt.formatWeight(secondary, this.weightSecondaryUnit)})`;
      }
      this.weightValueEl.textContent = text;
      this.weightEl.removeAttribute('hidden');
    }

    /**
     * Slot-count readout (chain diagrams). Manufacturing cut rule: slings
     * are cut from the middle of a slot to the NEXT slot middle past the
     * specified length, so a piece of length L holds
     * floor(L_mm / period) whole slots. Assumes the length value is in
     * METERS (true for every chain product today; see bc-diagram-slots).
     */
    _renderSlots(length, numericLength) {
      if (!this.slotsEl || !this.slotsValueEl) return;
      const meters = numericLength ? parseFloat(length) : NaN;
      if (!(this.slotPeriodMm > 0) || !Number.isFinite(meters) || meters <= 0) {
        this.slotsValueEl.textContent = '';
        this.slotsEl.setAttribute('hidden', '');
        return;
      }
      const slots = Math.floor((meters * 1000) / this.slotPeriodMm);
      this.slotsValueEl.textContent = `${formatNum(slots)} slots`;
      this.slotsEl.removeAttribute('hidden');
    }

    _findSelected(optionName) {
      // Shared option-by-display-name reader (assets/bc-variant-resolve.js).
      // '' (never null) so callers can chain with || — see _update.
      return window.bcVariantResolve?.readSelectedOption(this._scope, optionName) || '';
    }

    /**
     * Read the selected sewn-loop kind for the given role ('end1' / 'end2')
     * from the metaobject map. Returns 'none' | 'naked' | 'sleeved' | null.
     * Null means no migrated link is present for this role -- caller should
     * fall back to legend-text matching.
     */
    _findSewnLoopKind(role) {
      const entry = this._sewnLoopMap?.[role];
      if (!entry || !Number.isFinite(entry.position)) return null;
      // fieldset position is 1-indexed (Liquid product_option.position).
      // The diagram lives inside the variant-picker form, where each option
      // emits a fieldset with data-fieldset-index = position - 1 (see
      // snippets/variant-main-picker.liquid). Match by that index so we're
      // pinned to the option's structural slot, not its display name.
      const idx = entry.position - 1;
      const fieldset = this._scope.querySelector(
        `fieldset.variant-option[data-fieldset-index="${idx}"]`
      );
      if (!fieldset) return null;
      const radio = fieldset.querySelector('input[type="radio"]:checked');
      if (!radio) return null;
      const valueId = radio.dataset.optionValueId;
      if (!valueId) return null;
      const kind = entry.valueMap[valueId];
      return typeof kind === 'string' ? kind.toLowerCase() : null;
    }

    /**
     * Read the selected sewn-loop kind for the given role ('end1' / 'end2')
     * from the bc-end-treatment block's line-item-property selectors. Returns
     * 'none' | 'naked' | 'sleeved', or null when this product has no such
     * selector (callers fall back to the variant-option detection below). This
     * is the post-migration source — ends live as line item properties rather
     * than variant options, so the diagram reads the checked radio's data-kind
     * directly. Selectors are siblings of the variant picker inside the section
     * scope, so their `change` events already drive `_update` via the scope
     * listener installed in connectedCallback.
     */
    _findEndTreatmentProperty(role) {
      const group = this._scope.querySelector?.(`[data-bc-end-role="${role}"]`);
      if (!group) return null;
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) return (checked.dataset.kind || '').toLowerCase() || null;
      const select = group.querySelector('select');
      if (select) {
        const opt = select.options[select.selectedIndex];
        return (opt?.dataset.kind || '').toLowerCase() || null;
      }
      return null;
    }

    /**
     * The selected color variant's hex from the variant-color map, or null
     * when the product has no map / the selection doesn't resolve. Null
     * means "leave the Liquid-rendered colors alone". `value` may be passed
     * in when the caller has already read the option (_update needs the raw
     * value too, for the two-tone underside) so the picker is walked once.
     */
    _variantColor(value) {
      if (!this._colorMap) return null;
      const selected =
        value === undefined ? this._findSelected(this._colorMap.option || 'Color') : value;
      const hex = selected && this._colorMap.colors[selected];
      return typeof hex === 'string' && hex ? hex : null;
    }

    /**
     * Rebuild the chain body to fill 15..(viewBox width - 15): N water-drop
     * links (pointed ovals — two mirrored cubics meeting on the centerline,
     * so each loop tapers into its connectors) + N-1 connector lines,
     * connector length stretched so the end links stay anchored to both
     * edges. Mirrors the static Liquid render in bc-diagram-chain-svg.liquid
     * — keep the two in lockstep.
     */
    _rebuildChain(color) {
      if (!this.chainEl) return;
      const left = 15;
      const span = this._viewBoxWidth - 30;
      const ovalW = parseFloat(this.chainEl.dataset.ovalW) || 48;
      const ovalH = parseFloat(this.chainEl.dataset.ovalH) || 20;
      const connMin = parseFloat(this.chainEl.dataset.connMin) || 14;
      let n = Math.floor((span + connMin) / (ovalW + connMin));
      if (n < 2) n = 2;
      const connW = (span - n * ovalW) / (n - 1);
      const period = ovalW + connW;
      // A symmetric cubic peaks at 3/4 of its control offset; /0.75 keeps
      // the visible bulge height at exactly ovalH. The 0.32 sets the tip
      // angle — keep in lockstep with the snippet's ctrl_dx.
      const ctrlDy = ovalH / 2 / 0.75;
      const ctrlDx = ovalW * 0.32;
      const ns = 'http://www.w3.org/2000/svg';
      this.chainEl.replaceChildren();
      for (let i = 0; i < n; i++) {
        const x = left + i * period;
        const x1 = x + ovalW;
        const cxa = x + ctrlDx;
        const cxb = x1 - ctrlDx;
        const link = document.createElementNS(ns, 'path');
        link.setAttribute(
          'd',
          `M ${x} 92 C ${cxa} ${92 - ctrlDy} ${cxb} ${92 - ctrlDy} ${x1} 92 ` +
            `C ${cxb} ${92 + ctrlDy} ${cxa} ${92 + ctrlDy} ${x} 92 Z`
        );
        link.setAttribute('fill', 'none');
        link.setAttribute('stroke', color);
        link.setAttribute('stroke-width', String(this.lineThickness));
        link.setAttribute('stroke-linejoin', 'round');
        this.chainEl.appendChild(link);
        if (i < n - 1) {
          const conn = document.createElementNS(ns, 'line');
          conn.setAttribute('x1', String(x1));
          conn.setAttribute('y1', '92');
          conn.setAttribute('x2', String(x + period));
          conn.setAttribute('y2', '92');
          conn.setAttribute('stroke', color);
          conn.setAttribute('stroke-width', String(this.lineThickness));
          conn.setAttribute('stroke-linecap', 'butt');
          this.chainEl.appendChild(conn);
        }
      }
    }

    _applyEnd(group, value) {
      // Colors live in the SVG markup (stroke + linearGradient defs);
      // this only toggles visibility on the loop group and on the
      // outer-half sleeve overlay inside it.
      if (!group) return;
      const v = (value || '').toLowerCase().trim();
      const sleeveEl = group.querySelector('[data-sleeve]');
      if (v === END_NAKED) {
        group.removeAttribute('hidden');
        if (sleeveEl) sleeveEl.setAttribute('hidden', '');
      } else if (v === END_SLEEVED) {
        group.removeAttribute('hidden');
        if (sleeveEl) sleeveEl.removeAttribute('hidden');
      } else {
        group.setAttribute('hidden', '');
      }
    }

    _applyOverlap(group, value) {
      // Sewn-overlap stripe — visible whenever the end has a loop
      // (Naked or Sleeved). Hidden when the end is None, since there's
      // nothing to sew.
      if (!group) return;
      const v = (value || '').toLowerCase().trim();
      if (v === END_NAKED || v === END_SLEEVED) {
        group.removeAttribute('hidden');
      } else {
        group.setAttribute('hidden', '');
      }
    }

    _lineX(side, value) {
      // Webbing line endpoints. When the end has a loop, the line stops 2
      // units PAST the loop's connection point (so the line is drawn over
      // the loop's tip area — the loop visually tucks under the line at
      // the join). When None, the line extends all the way to the
      // diagram's outer extent (15 / right-15) instead. Right side is
      // anchored to the (possibly resized) right edge of the viewBox;
      // left side stays at fixed coords near the origin. loopOffset
      // shifts the endpoints with the loop translate so the join stays
      // flush in compact (mini) mode.
      const v = (value || '').toLowerCase().trim();
      const isLoop = v === END_NAKED || v === END_SLEEVED;
      if (side === 'left') return isLoop ? 78 - this.loopOffset : 15;
      const right = this._viewBoxWidth;
      return isLoop ? right - 78 + this.loopOffset : right - 15;
    }

    _updateDimension(hasLength) {
      if (!this.dimGroup) return;
      if (!hasLength) {
        this.dimGroup.setAttribute('hidden', '');
        return;
      }
      this.dimGroup.removeAttribute('hidden');

      // Dimension always spans the full outer extent of the diagram (loop
      // tip x), regardless of end style — visual consistency across states.
      const leftX = 15;
      const rightX = this._viewBoxWidth - 15;
      const centerX = this._viewBoxWidth / 2;
      const y = 38;
      const extTopY = 28;
      const extBottomY = 57;
      const arrowLen = 8;
      const arrowHalfH = 4;
      const textPadding = 6;

      if (this.dimExtLeft) {
        this.dimExtLeft.setAttribute('x1', String(leftX));
        this.dimExtLeft.setAttribute('x2', String(leftX));
        this.dimExtLeft.setAttribute('y1', String(extTopY));
        this.dimExtLeft.setAttribute('y2', String(extBottomY));
      }
      if (this.dimExtRight) {
        this.dimExtRight.setAttribute('x1', String(rightX));
        this.dimExtRight.setAttribute('x2', String(rightX));
        this.dimExtRight.setAttribute('y1', String(extTopY));
        this.dimExtRight.setAttribute('y2', String(extBottomY));
      }
      if (this.dimArrowLeft) {
        this.dimArrowLeft.setAttribute(
          'points',
          `${leftX},${y} ${leftX + arrowLen},${y - arrowHalfH} ${leftX + arrowLen},${y + arrowHalfH}`
        );
      }
      if (this.dimArrowRight) {
        this.dimArrowRight.setAttribute(
          'points',
          `${rightX},${y} ${rightX - arrowLen},${y - arrowHalfH} ${rightX - arrowLen},${y + arrowHalfH}`
        );
      }

      let halfGap = 0;
      if (this.lengthEl && (this.lengthEl.textContent || '').trim()) {
        try {
          const bbox = this.lengthEl.getBBox();
          if (bbox && bbox.width > 0) halfGap = bbox.width / 2 + textPadding;
        } catch (_e) {
          halfGap = 0;
        }
      }
      const leftEnd = halfGap > 0 ? Math.max(leftX, centerX - halfGap) : centerX;
      const rightStart = halfGap > 0 ? Math.min(rightX, centerX + halfGap) : centerX;
      if (this.dimLineLeft) {
        this.dimLineLeft.setAttribute('x1', String(leftX));
        this.dimLineLeft.setAttribute('x2', String(leftEnd));
        this.dimLineLeft.setAttribute('y1', String(y));
        this.dimLineLeft.setAttribute('y2', String(y));
      }
      if (this.dimLineRight) {
        this.dimLineRight.setAttribute('x1', String(rightStart));
        this.dimLineRight.setAttribute('x2', String(rightX));
        this.dimLineRight.setAttribute('y1', String(y));
        this.dimLineRight.setAttribute('y2', String(y));
      }
    }

    _update() {
      // Geometry writes parked by the ResizeObserver land first: everything
      // below is authored against _viewBoxWidth, and the key includes it.
      if (this._pendingViewBox != null) this._applyResponsiveLayout();

      let length = this._findSelected(this.lengthOption);
      // Source preference, highest first (?? not || so a real kind of
      // 'none' is never overridden by a later path on a falsey-but-valid
      // result):
      //   1. line-item-property selectors (bc-end-treatment) — post-migration
      //   2. sewn_loop_end metaobject map — variant-option products, migrated
      //   3. legacy legend-text matching — variant-option products, un-migrated
      //   4. data-end1-fixed / data-end2-fixed — products whose loop ends are
      //      a fixed fact of the product rather than a customer choice (e.g.
      //      the Webbing Extender: one sleeved + one naked loop, always).
      //      Emitted by bc-diagram-attrs.liquid from the custom.diagram
      //      override metafield. || not ?? — _findSelected returns '' (never
      //      null) when the product has no matching option, and a real
      //      selection from 1-3 must always win over the static fallback.
      const end1 =
        (this._findEndTreatmentProperty('end1') ??
          this._findSewnLoopKind('end1') ??
          this._findSelected(this.end1Option)) ||
        this.dataset.end1Fixed ||
        '';
      const end2 =
        (this._findEndTreatmentProperty('end2') ??
          this._findSewnLoopKind('end2') ??
          this._findSelected(this.end2Option)) ||
        this.dataset.end2Fixed ||
        '';

      // When a custom length is active, override the variant option value
      // with the user-entered length (and unit).
      let lengthUnit = this.lengthUnit;
      if (this._customLengthActive && this._customLength != null && this._customLength > 0) {
        length = String(this._customLength);
        if (this._customLengthUnit) lengthUnit = this._customLengthUnit;
      }

      const numericLength = length != null && !Number.isNaN(parseFloat(length));
      const pieces = this._customLengthActive
        ? this._customLengthPieces
        : this._readQuantity();

      // Variant-linked color: the hex re-strokes the marked webbing nodes
      // below and is the chain build color. `colorValue` is the raw selected
      // option value, which additionally picks the two-tone underside — read
      // once and handed to _variantColor so the picker isn't walked twice.
      const colorValue = this._colorMap
        ? this._findSelected(this._colorMap.option || 'Color')
        : '';
      const variantColor = this._variantColor(colorValue);
      // Only resolved when a weight readout exists — on every other instance
      // this would be two DOM queries for a value nothing renders.
      const weightVariantId = this._hasWeightReadout() ? this._resolveWeightVariantId() : '';

      // Change guard. Every input the render below reads is in this key; if
      // it matches what is already drawn, there is nothing to do. See the
      // header comment for why the key lives on the element.
      const renderKey = [
        length,
        lengthUnit,
        end1,
        end2,
        pieces,
        this._customLengthActive ? 1 : 0,
        variantColor || '',
        colorValue || '',
        weightVariantId,
        this._viewBoxWidth,
      ].join(KEY_SEP);
      if (this.getAttribute(RENDER_KEY_ATTR) === renderKey) return;
      this.setAttribute(RENDER_KEY_ATTR, renderKey);

      if (this.lengthEl) {
        // Only render a length string when it parses as a number — otherwise
        // a value like "Custom Length - In Meters" would land in the SVG.
        let text = '';
        if (numericLength) {
          // Split the leading number from any unit baked into the variant value
          // (e.g. "50m" -> "50" + "m") so we can thousands-group the number AND
          // put a space before the unit, matching custom lengths ("10 m").
          // Custom/unitless lengths have no suffix and pick up lengthUnit below.
          const raw = String(length).trim();
          const parts = raw.match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/);
          let lengthDisplay = raw;
          if (parts) {
            const grouped = formatNum(parseFloat(parts[1]));
            // Round-sling variant values carry a converted parenthetical
            // ("4 ft (1.2 m)") — drop it from the on-line readout; the
            // conversion re-enters via the secondary line when enabled.
            // Sling renders only (slingOvalEl), so webbing values keep
            // any parenthetical a future variant might carry.
            const suffix = this.slingOvalEl
              ? parts[2].trim().replace(/\s*\([^)]*\)$/, '')
              : parts[2].trim();
            lengthDisplay = suffix ? `${grouped} ${suffix}` : grouped;
          }
          text = lengthUnit ? `${lengthDisplay} ${lengthUnit}` : lengthDisplay;
          if (pieces > 1) text += ` × ${pieces}`;
        }
        this.lengthEl.textContent = text;
      }

      if (this.secondaryEl) {
        const showSecondary =
          this.secondaryEnabled && numericLength && this.secondaryFactor > 0;
        if (showSecondary) {
          const converted = parseFloat(length) * this.secondaryFactor;
          const rounded =
            converted >= 10
              ? Math.round(converted)
              : Math.round(converted * 10) / 10;
          this.secondaryEl.textContent = this.secondaryUnit
            ? `${formatNum(rounded)} ${this.secondaryUnit}`
            : formatNum(rounded);
          this.secondaryEl.removeAttribute('hidden');
        } else {
          this.secondaryEl.textContent = '';
          this.secondaryEl.setAttribute('hidden', '');
        }
      }
      this._applyEnd(this.end1El, end1);
      this._applyEnd(this.end2El, end2);
      this._applyOverlap(this.overlapLeftEl, end1);
      this._applyOverlap(this.overlapRightEl, end2);

      const x1 = String(this._lineX('left', end1));
      const x2 = String(this._lineX('right', end2));
      this.lineEls?.forEach((el) => {
        el.setAttribute('x1', x1);
        el.setAttribute('x2', x2);
      });

      // Re-stroke the marked webbing nodes with the variant color read in the
      // pass above; the chain body takes the color at (re)build time.
      // Rebuilding here also covers responsive resizes — a new viewBox width
      // changes the key, so this pass always runs after one.
      if (variantColor) {
        this.querySelectorAll('[data-bc-vc="stroke"]').forEach((el) => {
          el.setAttribute('stroke', variantColor);
        });
        // Per-variant two-tone underside — only when the map carries a
        // colorsLower object (custom.diagram override products). Absent map
        // = the lower color is a static product-level fact (e.g. Paradigm's
        // webbing.line_color_lower) and must never follow the variant.
        // Variants without an entry collapse to the upper color, which is
        // visually identical to a single-tone render.
        const lowerColors = this._colorMap?.colorsLower;
        if (lowerColors && typeof lowerColors === 'object') {
          const lowerHex =
            (colorValue && typeof lowerColors[colorValue] === 'string' && lowerColors[colorValue]) ||
            variantColor;
          this.querySelectorAll('[data-bc-vc-lower="stroke"]').forEach((el) => {
            el.setAttribute('stroke', lowerHex);
          });
        }
      }
      this._rebuildChain(variantColor || this.lineColor);

      // Only dimension when we have a numeric length to show.
      this._updateDimension(numericLength);

      this._renderWeight();
      this._renderSlots(length, numericLength);
    }
  }

  customElements.define('bc-diagram', BcDiagram);
})();
